import { apiService, API_ENDPOINTS, buildEndpoint } from "@/shared/services/api"
import type { APIResponse } from "@/shared/types"
import type { SalesReport, PayReport } from "@/features/agents"
import type { User, Agent } from "@/features/auth"
import type { CommissionRate, OverrideRate, CommissionRateValidationResult, CommissionRateFilters, CommissionRateStats } from "../types"

export class HRService {
  // Sales Reports Management
  static async getSalesReports(filters?: {
    organizationId?: string
    campaignId?: string
    agentId?: string
    status?: string
    campaignName?: string
    minAmount?: number
    maxAmount?: number
    dateFrom?: string
    dateTo?: string
    page?: number
    limit?: number
  }, hrManagerOrgId?: string): Promise<{ reports: SalesReport[]; total: number; page: number; limit: number; totalPages: number }> {
    // For HR managers, enforce organization filtering
    // For FMO (no hrManagerOrgId), allow viewing all organizations
    const effectiveFilters = { ...filters }
    if (hrManagerOrgId) {
      effectiveFilters.organizationId = hrManagerOrgId
    }

    const params = new URLSearchParams()
    Object.entries(effectiveFilters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value))
      }
    })

    const response = await apiService.get<APIResponse<{ reports: SalesReport[]; total: number; page: number; limit: number; totalPages: number }>>(
      `${API_ENDPOINTS.hr.salesReports}?${params.toString()}`,
    )
    return response.data.data!
  }

  static async getSalesReportsStats(filters?: {
    organizationId?: string
    campaignId?: string
    agentId?: string
    status?: string
    campaignName?: string
    minAmount?: number
    maxAmount?: number
    dateFrom?: string
    dateTo?: string
  }): Promise<{
    totalReports: number
    totalAmount: number
    pendingReports: number
    approvedReports: number
    rejectedReports: number
    campaignStats: Array<{ campaignName: string; _count: { id: number }; _sum: { amount: number } }>
  }> {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value) params.append(key, String(value))
    })

    const response = await apiService.get<APIResponse<{
      totalReports: number
      totalAmount: number
      pendingReports: number
      approvedReports: number
      rejectedReports: number
      campaignStats: Array<{ campaignName: string; _count: { id: number }; _sum: { amount: number } }>
    }>>(
      `${API_ENDPOINTS.hr.salesReportsStats}?${params.toString()}`,
    )
    return response.data.data!
  }

  static async getSalesReportsByAgent(agentId: string, filters?: {
    status?: string
    campaignName?: string
    minAmount?: number
    maxAmount?: number
    dateFrom?: string
    dateTo?: string
    page?: number
    limit?: number
  }): Promise<{ reports: SalesReport[]; total: number; page: number; limit: number; totalPages: number }> {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value) params.append(key, String(value))
    })

    const response = await apiService.get<APIResponse<{ reports: SalesReport[]; total: number; page: number; limit: number; totalPages: number }>>(
      `${API_ENDPOINTS.hr.salesReportsByAgent.replace(':agentId', agentId)}?${params.toString()}`,
    )
    return response.data.data!
  }

  static async getSalesReportsByOrganization(organizationId: string, filters?: {
    status?: string
    campaignName?: string
    minAmount?: number
    maxAmount?: number
    dateFrom?: string
    dateTo?: string
    page?: number
    limit?: number
  }): Promise<{ reports: SalesReport[]; total: number; page: number; limit: number; totalPages: number }> {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value) params.append(key, String(value))
    })

    const response = await apiService.get<APIResponse<{ reports: SalesReport[]; total: number; page: number; limit: number; totalPages: number }>>(
      `${API_ENDPOINTS.hr.salesReportsByOrganization.replace(':organizationId', organizationId)}?${params.toString()}`,
    )
    return response.data.data!
  }

  static async updateSalesReport(id: string, data: {
    status?: string
    amount?: number
    totalSubmissions?: number
    acceptedSubmissions?: number
    rejectedSubmissions?: number
    inProgressSubmissions?: number
    downlineCountActive?: number
    downlineCountInactive?: number
    totalDownlineSubmissions?: number
  }): Promise<SalesReport> {
    const response = await apiService.put<APIResponse<SalesReport>>(
      buildEndpoint(API_ENDPOINTS.hr.salesReportsById, { id }),
      data,
    )
    return response.data.data!
  }

  static async getSalesReportById(id: string): Promise<SalesReport> {
    const response = await apiService.get<APIResponse<SalesReport>>(
      buildEndpoint(API_ENDPOINTS.hr.salesReportsById, { id }),
    )
    return response.data.data!
  }

  static async getSalesReportsSummary(): Promise<any> {
    // Use the stats endpoint instead
    const response = await apiService.get<APIResponse<any>>(API_ENDPOINTS.hr.salesReportsStats)
    return response.data.data!
  }


  static async bulkUpdatePayReports(data: {
    ids: string[]
    status: string
  }): Promise<void> {
    await apiService.put<APIResponse>(API_ENDPOINTS.hr.payReportsBulkUpdate, data)
  }

  static async correctPayReportAmount(id: string, newAmount: number): Promise<PayReport> {
    const response = await apiService.put<APIResponse<PayReport>>(
      buildEndpoint(API_ENDPOINTS.hr.payReportsCorrectAmount, { id }),
      { newAmount },
    )
    return response.data.data!
  }

  static async getPayReportsSummary(): Promise<any> {
    const response = await apiService.get<APIResponse<any>>(API_ENDPOINTS.hr.payReportsSummary)
    return response.data.data!
  }

  // Team Management
  static async assignAgentToTeam(data: {
    agentId: string
    organizationId: string
  }): Promise<void> {
    await apiService.post<APIResponse>(API_ENDPOINTS.hr.agentAssignments.assign, data)
  }

  static async batchAssignAgents(data: {
    agentIds: string[]
    organizationId: string
  }): Promise<void> {
    await apiService.post<APIResponse>(API_ENDPOINTS.hr.agentAssignments.batchAssign, data)
  }

  static async getAgents(filters?: {
    teamId?: string
    status?: string
    search?: string
  }, hrManagerOrgId?: string): Promise<Agent[]> {
    // For HR managers, enforce organization filtering
    const effectiveFilters = {
      ...filters,
      organizationId: hrManagerOrgId
    }
    const params = new URLSearchParams()
    Object.entries(effectiveFilters || {}).forEach(([key, value]) => {
      if (value) params.append(key, String(value))
    })

    const response = await apiService.get<APIResponse<Agent[]>>(`${API_ENDPOINTS.hr.agents}?${params.toString()}`)
    return response.data.data!
  }

  static async getTeams(): Promise<any[]> {
    const response = await apiService.get<APIResponse<any[]>>(API_ENDPOINTS.hr.teams)
    return response.data.data!
  }

  static async createAgent(data: {
    email: string
    firstName: string
    lastName: string
    phone?: string
    agentId?: string
    managerName?: string
    managerNameSource?: string
    managerEmail?: string
    managerEmailSource?: string
  }): Promise<any> {
    const response = await apiService.post<APIResponse<any>>(API_ENDPOINTS.hr.agents, data)
    return response.data.data!
  }

  static async updateAgent(agentId: string, data: {
    email?: string
    firstName?: string
    lastName?: string
    phone?: string
    managerName?: string
    managerNameSource?: string
    managerEmail?: string
    managerEmailSource?: string
  }): Promise<any> {
    const response = await apiService.put<APIResponse<any>>(
      `/hr/agents/${agentId}`,
      data,
    )
    return response.data.data!
  }


  // Agent Assignment Management
  static async getAgentsForAssignment(filters?: {
    status?: string
    search?: string
    organizationId?: string
    teamId?: string
    page?: number
    limit?: number
  }): Promise<{
    agents: any[]
    total: number
    page: number
    limit: number
    totalPages: number
  }> {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value) params.append(key, String(value))
    })

    const response = await apiService.get<APIResponse<{
      agents: any[]
      total: number
      page: number
      limit: number
      totalPages: number
    }>>(`${API_ENDPOINTS.hr.agentAssignments.agents}?${params.toString()}`)
    return response.data.data!
  }


  static async getTeamsForAssignment(filters?: {
    organizationId?: string
    organizationLevel?: string
    parentId?: string
    eLevel?: string
    dLevel?: string
    cLevel?: string
    bLevel?: string
  }): Promise<any[]> {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value) params.append(key, String(value))
    })

    const response = await apiService.get<APIResponse<any[]>>(`${API_ENDPOINTS.hr.agentAssignments.teams}?${params.toString()}`)
    return response.data.data!
  }


  // Agent Status Management
  static async updateAgentStatus(agentId: string, status: "activate" | "deactivate" | "terminate"): Promise<any> {
    const response = await apiService.put<APIResponse<any>>(buildEndpoint(API_ENDPOINTS.hr.updateAgentStatus, { id: agentId }), { status })
    return response.data.data!
  }

  static async activateAgent(id: string): Promise<void> {
    await apiService.put<APIResponse>(buildEndpoint(API_ENDPOINTS.hr.activateAgent, { id }), {})
  }

  static async deactivateAgent(id: string): Promise<void> {
    await apiService.put<APIResponse>(buildEndpoint(API_ENDPOINTS.hr.deactivateAgent, { id }), {})
  }

  static async terminateAgent(id: string): Promise<void> {
    await apiService.put<APIResponse>(buildEndpoint(API_ENDPOINTS.hr.terminateAgent, { id }), {})
  }

  static async reassignAgent(data: {
    agentId: string
    newManagerId: string
  }): Promise<void> {
    await apiService.post<APIResponse>(API_ENDPOINTS.hr.reassignAgent, data)
  }

  static async getAgentStatusHistory(id: string): Promise<any[]> {
    const response = await apiService.get<APIResponse<any[]>>(
      buildEndpoint(API_ENDPOINTS.hr.agentStatusHistory, { id }),
    )
    return response.data.data!
  }

  // Commission Rates Management - Delegated to CommissionRateService
  static async getCommissionRates(filters?: {
    organizationId?: string
    campaignId?: string
    agentId?: string
    jobType?: "SALES_AGENT" | "LEAD_GENERATION" | "EXECUTIVE_AGENT"
    isActive?: boolean
    minCommissionRate?: number
    maxCommissionRate?: number
    minOverrideRate?: number
    maxOverrideRate?: number
    enableSalesTier?: boolean
    enableOverrideTier?: boolean
    page?: number
    limit?: number
  }): Promise<{
    commissionRates: CommissionRate[]
    total: number
    page: number
    limit: number
    totalPages: number
  }> {
    const { CommissionRateService } = await import('./commissionRate.service')
    return CommissionRateService.getCommissionRates(filters)
  }

  static async getCommissionRateStats(filters?: {
    organizationId?: string
    campaignId?: string
    agentId?: string
    jobType?: "SALES_AGENT" | "LEAD_GENERATION" | "EXECUTIVE_AGENT"
    isActive?: boolean
    minCommissionRate?: number
    maxCommissionRate?: number
    minOverrideRate?: number
    maxOverrideRate?: number
    enableSalesTier?: boolean
    enableOverrideTier?: boolean
  }): Promise<CommissionRateStats> {
    const { CommissionRateService } = await import('./commissionRate.service')
    return CommissionRateService.getCommissionRateStats(filters)
  }

  static async createCommissionRate(data: {
    organizationId: string
    campaignId: string
    agentId: string
    commissionRate: number
    jobType: "SALES_AGENT" | "LEAD_GENERATION" | "EXECUTIVE_AGENT"
    enableSalesTier: boolean
    volumeTier1Min: number
    volumeTier1Max: number
    tier1PayRate: number
    volumeTier2Min: number
    volumeTier2Max: number
    tier2PayRate: number
    volumeTier3Min: number
    volumeTier3Max: number
    tier3PayRate: number
    overrideRate: number
    enableOverrideTier: boolean
    overrideVolumeTier1Min: number
    overrideVolumeTier1Max: number
    overrideTier1PayRate: number
    overrideVolumeTier2Min: number
    overrideVolumeTier2Max: number
    overrideTier2PayRate: number
    overrideVolumeTier3Min: number
    overrideVolumeTier3Max: number
    overrideTier3PayRate: number
    isActive: boolean
  }): Promise<{ success: boolean; message: string; data?: CommissionRate; warnings?: string[]; errors?: string[] }> {
    const { CommissionRateService } = await import('./commissionRate.service')
    return CommissionRateService.createCommissionRate(data)
  }

  static async updateCommissionRate(id: string, data: Partial<CommissionRate>): Promise<CommissionRateValidationResult & { data?: CommissionRate }> {
    const { CommissionRateService } = await import('./commissionRate.service')
    return CommissionRateService.updateCommissionRate(id, data)
  }

  static async deleteCommissionRate(id: string): Promise<void> {
    const { CommissionRateService } = await import('./commissionRate.service')
    return CommissionRateService.deleteCommissionRate(id)
  }

  static async getAgentCommissionRates(agentId: string, filters?: Omit<CommissionRateFilters, 'agentId'>): Promise<{
    commissionRates: CommissionRate[]
    total: number
    page: number
    limit: number
    totalPages: number
  }> {
    const { CommissionRateService } = await import('./commissionRate.service')
    return CommissionRateService.getAgentCommissionRates(agentId, filters)
  }

  // Override Rates Management
  static async getOverrideRates(filters?: {
    uplineAgentId?: string
    downlineAgentId?: string
    generation?: number
  }): Promise<OverrideRate[]> {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value) params.append(key, String(value))
    })

    const response = await apiService.get<APIResponse<OverrideRate[]>>(
      `${API_ENDPOINTS.hr.overrideRates}?${params.toString()}`,
    )
    return response.data.data!
  }

  static async setOverrideRate(data: {
    uplineAgentId: string
    downlineAgentId: string
    generation: number
    percentage: number
  }): Promise<OverrideRate> {
    const response = await apiService.post<APIResponse<OverrideRate>>(API_ENDPOINTS.hr.overrideRates, data)
    return response.data.data!
  }

  static async updateOverrideRate(id: string, data: Partial<OverrideRate>): Promise<OverrideRate> {
    const response = await apiService.put<APIResponse<OverrideRate>>(
      buildEndpoint(API_ENDPOINTS.hr.overrideRatesById, { id }),
      data,
    )
    return response.data.data!
  }

  static async deleteOverrideRate(id: string): Promise<void> {
    await apiService.delete<APIResponse>(buildEndpoint(API_ENDPOINTS.hr.overrideRatesById, { id }))
  }

  static async getAgentOverrideRates(agentId: string): Promise<OverrideRate[]> {
    const response = await apiService.get<APIResponse<OverrideRate[]>>(
      API_ENDPOINTS.hr.overrideRatesByAgent.replace(':agentId', agentId)
    )
    return response.data.data!
  }

  // Device Management
  static async initiateDeviceReregistration(data: {
    targetUserId: string
    visitorId: string
    confidence: number
    deviceName?: string
    userAgent?: string
    platform?: string
    screenResolution?: string
    timezone?: string
    language?: string
    browserFingerprint?: Record<string, any>
  }): Promise<void> {
    await apiService.post<APIResponse>(API_ENDPOINTS.hr.initiateDeviceReregistration, data)
  }

  static async getUserDevices(userId: string): Promise<any[]> {
    const response = await apiService.get<APIResponse<any[]>>(
      buildEndpoint(API_ENDPOINTS.hr.userDevices, { id: userId }),
    )
    return response.data.data!
  }

  static async deactivateDevice(deviceId: string): Promise<void> {
    await apiService.put<APIResponse>(buildEndpoint(API_ENDPOINTS.hr.deactivateDevice, { id: deviceId }), {})
  }

  static async getDeviceHistory(deviceId: string): Promise<any[]> {
    const response = await apiService.get<APIResponse<any[]>>(
      buildEndpoint(API_ENDPOINTS.hr.deviceHistory, { id: deviceId }),
    )
    return response.data.data!
  }

  // HR Settings
  static async toggleAgentPayReportView(enabled: boolean): Promise<void> {
    await apiService.put<APIResponse>(API_ENDPOINTS.hr.toggleAgentPayReportView, { enabled })
  }

  static async getHRSettings(): Promise<any> {
    const response = await apiService.get<APIResponse<any>>(API_ENDPOINTS.hr.settings)
    return response.data.data!
  }

  static async updateHRSettings(settings: any): Promise<any> {
    const response = await apiService.put<APIResponse<any>>(API_ENDPOINTS.hr.settings, settings)
    return response.data.data!
  }

  // Analytics and Reports
  static async getDashboardStats(): Promise<{
    activeAgents: number
    totalSales: number
    pendingReviews: number
    totalTeams: number
    totalRevenue: number
    pendingPayReports: number
    activeTeams: number
    deactivatedAgents: number
  }> {
    const response = await apiService.get<APIResponse<any>>(API_ENDPOINTS.hr.dashboardStats)
    return response.data.data!
  }

  static async getTeamPerformance(): Promise<any[]> {
    const response = await apiService.get<APIResponse<any[]>>(API_ENDPOINTS.hr.teamPerformance)
    return response.data.data!
  }

  static async getRecentActions(): Promise<any[]> {
    const response = await apiService.get<APIResponse<any[]>>(API_ENDPOINTS.hr.recentActions)
    return response.data.data!
  }

  // Notifications & Workflows
  static async getNotifications(): Promise<any[]> {
    const response = await apiService.get<APIResponse<any[]>>(API_ENDPOINTS.hr.notifications)
    return response.data.data!
  }

  static async getWorkflows(): Promise<any[]> {
    const response = await apiService.get<APIResponse<any[]>>(API_ENDPOINTS.hr.workflows)
    return response.data.data!
  }

  static async getApprovals(): Promise<any[]> {
    const response = await apiService.get<APIResponse<any[]>>(API_ENDPOINTS.hr.approvals)
    return response.data.data!
  }

  // Audit & Compliance
  static async getAuditLogs(filters?: {
    userId?: string
    action?: string
    startDate?: string
    endDate?: string
  }): Promise<any[]> {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value) params.append(key, String(value))
    })

    const response = await apiService.get<APIResponse<any[]>>(`${API_ENDPOINTS.hr.auditLogs}?${params.toString()}`)
    return response.data.data!
  }

  static async getComplianceReports(): Promise<any[]> {
    const response = await apiService.get<APIResponse<any[]>>(API_ENDPOINTS.hr.complianceReports)
    return response.data.data!
  }

  // Bulk Operations
  static async bulkUpdateAgents(data: {
    agentIds: string[]
    updates: Record<string, any>
  }): Promise<void> {
    await apiService.put<APIResponse>(API_ENDPOINTS.hr.bulkUpdateAgents, data)
  }

  static async bulkUpdateRates(data: {
    rateIds: string[]
    updates: Record<string, any>
  }): Promise<void> {
    await apiService.put<APIResponse>(API_ENDPOINTS.hr.bulkUpdateRates, data)
  }

  // Campaigns & Products
  static async getCampaigns(): Promise<any[]> {
    const response = await apiService.get<APIResponse<any[]>>(API_ENDPOINTS.hr.campaigns)
    return response.data.data!
  }

  static async getProducts(): Promise<any[]> {
    const response = await apiService.get<APIResponse<any[]>>(API_ENDPOINTS.hr.products)
    return response.data.data!
  }

  static async getCampaignRates(campaignId: string): Promise<any[]> {
    const response = await apiService.get<APIResponse<any[]>>(
      buildEndpoint(API_ENDPOINTS.hr.campaignRates, { id: campaignId }),
    )
    return response.data.data!
  }

  // Export Functions
  static async exportSalesReports(filters?: Record<string, any>): Promise<Blob> {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value))
      }
    })

    // Use the dedicated export endpoint which supports PDF and CSV
    const response = await apiService.get(`/sales-reports/export?${params.toString()}`, {
      responseType: "blob",
    })
    return response.data
  }

  static async exportPayReports(filters?: Record<string, any>): Promise<Blob> {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value) params.append(key, String(value))
    })

    const response = await apiService.get(`${API_ENDPOINTS.hr.exportPayReports}?${params.toString()}`, {
      responseType: "blob",
    })
    return response.data
  }

  // Organization Management
  static async getOrganizations(hrManagerOrgId?: string): Promise<any[]> {
    const response = await apiService.get<APIResponse<any[]>>(API_ENDPOINTS.hr.organizations)
    const organizations = response.data.data || []

    // For HR managers, filter to only their assigned organization
    if (hrManagerOrgId) {
      return organizations.filter(org => org.id === hrManagerOrgId)
    }

    return organizations
  }

  static async getOrganizationHierarchy(filters?: {
    type?: string
    parentId?: string
    includeTeams?: boolean
    includeAgentCounts?: boolean
  }): Promise<any[]> {
    const params = new URLSearchParams()
    if (filters?.type) params.append('type', filters.type)
    if (filters?.parentId) params.append('parentId', filters.parentId)
    if (filters?.includeTeams) params.append('includeTeams', filters.includeTeams.toString())
    if (filters?.includeAgentCounts) params.append('includeAgentCounts', filters.includeAgentCounts.toString())

    const response = await apiService.get<APIResponse<any[]>>(`${API_ENDPOINTS.hr.organizationHierarchy}?${params.toString()}`)
    return response.data.data || []
  }

  static async getAvailableOrganizationTypes(): Promise<APIResponse<{
    availableTypes: string[]
    hierarchy: Record<string, { name: string; canCreate: string[] }>
  }>> {
    const response = await apiService.get<APIResponse<{
      availableTypes: string[]
      hierarchy: Record<string, { name: string; canCreate: string[] }>
    }>>(API_ENDPOINTS.hr.organizationTypes)
    return response.data
  }

  static async createOrganization(data: {
    id?: string
    name: string
    type: string
    parentId?: string
    settings?: any
  }): Promise<APIResponse<any>> {
    const response = await apiService.post<APIResponse<any>>(API_ENDPOINTS.hr.organizations, data)
    return response.data
  }

  static async getOrganizationById(id: string): Promise<APIResponse<any>> {
    const response = await apiService.get<APIResponse<any>>(buildEndpoint(API_ENDPOINTS.hr.organizationsById, { id }))
    return response.data
  }

  static async updateOrganization(id: string, data: {
    name?: string
    type?: string
    parentId?: string
    settings?: any
  }): Promise<APIResponse<any>> {
    const response = await apiService.put<APIResponse<any>>(buildEndpoint(API_ENDPOINTS.hr.updateOrganization, { id }), data)
    return response.data
  }

  static async deleteOrganization(id: string): Promise<APIResponse<any>> {
    const response = await apiService.delete<APIResponse<any>>(buildEndpoint(API_ENDPOINTS.hr.deleteOrganization, { id }))
    return response.data
  }

  static async bulkReassignAgents(fromOrganizationId: string, toOrganizationId: string): Promise<APIResponse<{ reassignedCount: number }>> {
    const response = await apiService.post<APIResponse<{ reassignedCount: number }>>(API_ENDPOINTS.hr.bulkReassignAgents, {
      fromOrganizationId,
      toOrganizationId
    })
    return response.data
  }

  // Pay Reports Management
  static async getPayReports(filters?: {
    organizationId?: string
    agentId?: string
    teamId?: string
    status?: string
    payPeriodStart?: string
    payPeriodEnd?: string
    minCommission?: number
    maxCommission?: number
    page?: number
    limit?: number
  }): Promise<{ payReports: PayReport[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
    const params = new URLSearchParams()
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value) params.append(key, String(value))
    })

    const response = await apiService.get<APIResponse<{ payReports: PayReport[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>>(
      `${API_ENDPOINTS.hr.payReports}?${params.toString()}`,
    )
    return response.data.data!
  }

  static async updatePayReport(id: string, data: any): Promise<APIResponse<PayReport>> {
    const response = await apiService.put<APIResponse<PayReport>>(buildEndpoint(API_ENDPOINTS.hr.updatePayReport, { id }), data)
    return response.data
  }

  // Utility Methods
  static async downloadFile(blob: Blob, filename: string): Promise<void> {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  // Organization List ID Management
  static async getOrganizationListIds(): Promise<Array<{
    id: string
    name: string
    type: string
    listId: string | null
    isActive: boolean
    createdAt: string
    updatedAt: string
  }>> {
    const response = await apiService.get<APIResponse<Array<{
      id: string
      name: string
      type: string
      listId: string | null
      isActive: boolean
      createdAt: string
      updatedAt: string
    }>>>(API_ENDPOINTS.hr.organizationListIds)
    return response.data.data!
  }

  static async updateOrganizationListId(organizationId: string, listId: string): Promise<{
    id: string
    name: string
    type: string
    listId: string | null
    isActive: boolean
    updatedAt: string
  }> {
    const response = await apiService.put<APIResponse<{
      id: string
      name: string
      type: string
      listId: string | null
      isActive: boolean
      updatedAt: string
    }>>(`${API_ENDPOINTS.hr.organizationListIds}/${organizationId}`, { listId })
    return response.data.data!
  }

  static async bulkUpdateOrganizationListIds(updates: Array<{ organizationId: string, listId: string }>): Promise<{
    message: string
    updatedCount: number
    updatedOrganizations: Array<{
      id: string
      name: string
      type: string
      listId: string | null
    }>
  }> {
    const response = await apiService.post<APIResponse<{
      message: string
      updatedCount: number
      updatedOrganizations: Array<{
        id: string
        name: string
        type: string
        listId: string | null
      }>
    }>>(`${API_ENDPOINTS.hr.organizationListIds}/bulk-update`, { updates })
    return response.data.data!
  }

  static async getOrganizationsByListId(listId: string): Promise<Array<{
    id: string
    name: string
    type: string
    listId: string | null
    isActive: boolean
    createdAt: string
    updatedAt: string
  }>> {
    const response = await apiService.get<APIResponse<Array<{
      id: string
      name: string
      type: string
      listId: string | null
      isActive: boolean
      createdAt: string
      updatedAt: string
    }>>>(`${API_ENDPOINTS.hr.organizationListIds}/${listId}/organizations`)
    return response.data.data!
  }

  static async getOrganizationListIdStats(): Promise<Array<{
    listId: string
    count: number
  }>> {
    const response = await apiService.get<APIResponse<Array<{
      listId: string
      count: number
    }>>>(`${API_ENDPOINTS.hr.organizationListIds}/stats`)
    return response.data.data!
  }

  static async getAllOrganizationsWithListIds(): Promise<Array<{
    id: string
    name: string
    type: string
    listId: string | null
    isActive: boolean
    createdAt: string
    updatedAt: string
    listIdRef?: {
      id: string
      name: string
      color: string
    } | null
  }>> {
    const response = await apiService.get<APIResponse<Array<{
      id: string
      name: string
      type: string
      listId: string | null
      isActive: boolean
      createdAt: string
      updatedAt: string
      listIdRef?: {
        id: string
        name: string
        color: string
      } | null
    }>>>(`${API_ENDPOINTS.hr.base}/organizations-with-list-ids`)
    return response.data.data!
  }

  // List ID Management Functions
  static async getAllListIds(): Promise<Array<{
    id: string,
    name: string,
    description: string | null,
    color: string,
    isActive: boolean,
    isSystem: boolean,
    createdAt: string,
    updatedAt: string,
    _count: {
      organizations: number
    }
  }>> {
    const response = await apiService.get<APIResponse<Array<{
      id: string;
      name: string;
      description: string | null;
      color: string;
      isActive: boolean;
      isSystem: boolean;
      createdAt: string;
      updatedAt: string;
      _count: {
        organizations: number;
      };
    }>>>(`${API_ENDPOINTS.hr.base}/list-ids`)
    return response.data.data!
  }

  static async getListIdById(id: string): Promise<{
    id: string,
    name: string,
    description: string | null,
    color: string,
    isActive: boolean,
    isSystem: boolean,
    createdAt: string,
    updatedAt: string,
    organizations: Array<{
      id: string,
      name: string,
      type: string
    }>,
    _count: {
      organizations: number
    }
  }> {
    const response = await apiService.get<APIResponse<{
      id: string;
      name: string;
      description: string | null;
      color: string;
      isActive: boolean;
      isSystem: boolean;
      createdAt: string;
      updatedAt: string;
      organizations: Array<{
        id: string;
        name: string;
        type: string;
      }>;
      _count: {
        organizations: number;
      };
    }>>(`${API_ENDPOINTS.hr.base}/list-ids/${id}`)
    return response.data.data!
  }

  static async createListId(data: {
    name: string
    description?: string
    color?: string
  }): Promise<{
    id: string,
    name: string,
    description: string | null,
    color: string,
    isActive: boolean,
    isSystem: boolean,
    createdAt: string,
    updatedAt: string
  }> {
    const response = await apiService.post<APIResponse<{
      id: string;
      name: string;
      description: string | null;
      color: string;
      isActive: boolean;
      isSystem: boolean;
      createdAt: string;
      updatedAt: string;
    }>>(`${API_ENDPOINTS.hr.base}/list-ids`, data)
    return response.data.data!
  }

  static async updateListId(id: string, data: {
    name?: string
    description?: string
    color?: string
  }): Promise<{
    id: string,
    name: string,
    description: string | null,
    color: string,
    isActive: boolean,
    isSystem: boolean,
    createdAt: string,
    updatedAt: string
  }> {
    const response = await apiService.put<APIResponse<{
      id: string;
      name: string;
      description: string | null;
      color: string;
      isActive: boolean;
      isSystem: boolean;
      createdAt: string;
      updatedAt: string;
    }>>(`${API_ENDPOINTS.hr.base}/list-ids/${id}`, data)
    return response.data.data!
  }

  static async deleteListId(id: string): Promise<{
    message: string
  }> {
    const response = await apiService.delete<APIResponse<{
      message: string;
    }>>(`${API_ENDPOINTS.hr.base}/list-ids/${id}`)
    return response.data.data!
  }

  /**
   * Reset agent password
   */
  static async resetAgentPassword(agentId: string): Promise<APIResponse<{
    temporaryPassword: string
    email: string
    agentCode: string
    resetAt: string
  }>> {
    try {
      const response = await apiService.post(`/hr/agents/${agentId}/reset-password`)
      return response.data
    } catch (error: any) {
      console.error('Error resetting agent password:', error)
      throw new Error(error.response?.data?.message || 'Failed to reset agent password')
    }
  }
}
